import {Component, Inject, Input, OnDestroy, OnInit} from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import {TranslocoService} from "@ngneat/transloco";
import {StoreSelectorsService} from "../../services";
import {Subscription} from "rxjs";
import {take} from "rxjs/operators";

type IHotKeyMode = 'scene' | 'slide' | 'painter' | 'timeline' | 'scheduler';
interface IHotKey {
    key: string;
    description: string;
    hidden?: boolean;
}

interface IHotKeys {
    group: string;
    description?: string;
    keys: Array<IHotKey>;
}

interface IData {
    mode?: IHotKeyMode
}

@Component({
    selector: 'app-dialog-hotkeys',
    templateUrl: './dialog-hotkeys.component.html',
    styleUrls: ['./dialog-hotkeys.component.scss']
})
export class DialogHotkeysComponent implements OnInit, OnDestroy {

    @Input() mode: 'slideMode' | 'sceneMode' = 'sceneMode';
    hotkeys: Array<IHotKeys> = [];
    hotkeyMode: IHotKeyMode = 'scene';
    private subscription: Subscription;

    constructor(private translocoService: TranslocoService,
                private storeSelector: StoreSelectorsService,
                @Inject(MAT_DIALOG_DATA) public data: IData) {
        // Always use RxJS store editorMode
        this.subscription = this.storeSelector.getEditorMode().pipe(take(1)).subscribe(editorMode => {
            if (editorMode === 'touch-editor') {
                this.hotkeyMode = 'slide';
            } else if (editorMode === 'scene-builder') {
                this.hotkeyMode = 'scene';
            } else if (data?.mode) {
                // Fallback to data.mode for timeline/scheduler/painter
                this.hotkeyMode = data.mode;
            } else {
                // Final fallback to input mode
                this.hotkeyMode = this.mode === 'slideMode' ? 'slide' : 'scene';
            }
        });
    }

    ngOnInit(): void {
        switch(this.hotkeyMode) {
            case 'slide': {
                const hidden = false;
                this.hotkeys = [
                    {
                        group: this.translate('general.general'),
                        description: 'general hotkeys',
                        keys: [
                            {key: 'Ctrl + C', description: this.translate('general.copy')},
                            {key: 'Ctrl + X', description: this.translate('general.cut')},
                            {key: 'Ctrl + V', description: this.translate('general.paste')},

                            {key: 'Ctrl + Z', description: this.translate('general.undo')},
                            {key: 'Ctrl + Y', description: this.translate('general.redo')},

                            {key: 'Alt + Plus', description: this.translate('general.zoomIn')},
                            {key: 'Alt + Minus', description: this.translate('general.zoomOut')},

                            {key: 'Ctrl + L', description: this.translate('general.toggleGuide'), hidden},
                            {key: 'Ctrl + ?', description: this.translate('general.keyboard')},
                            {key: 'Esc', description: this.translate('general.selectScene')}
                        ]
                    },
                    {
                        group: this.translate('general.items'),
                        description: 'selected items hotkeys',
                        keys: [
                            {key: 'Ctrl, Shift + Mouse', description: this.translate('general.addToSelection2'), hidden},
                            {key: 'Delete', description: this.translate('general.removeItems')},
                            {key: 'Alt + T', description: this.translate('general.addLabel'), hidden},
                            {key: 'Alt + P', description: this.translate('general.goBackPreviousScene'), hidden},

                            {key: 'Alt + Up', description: this.translate('general.pushUp')},
                            {key: 'Alt + Down', description: this.translate('general.pushDown')},
                            {key: 'Alt + Shift + Up', description: this.translate('general.pushTop')},
                            {key: 'Alt + Shift + Down', description: this.translate('general.pushBottom')},

                            {key: 'Alt + LeftRight', description: this.translate('general.rotate') + ' (1&#176;)'},
                            {key: 'Alt + Shift + LeftRight', description: this.translate('general.rotate') + ' (45&#176;)'},
                            {key: 'Arrow', description: this.translate('scenes.move')},
                            {key: 'Shift + Arrow', description: this.translate('scenes.move') + ' x 10(px)'},
                            {key: 'Ctrl + Arrow', description: this.translate('scenes.resize'), hidden},
                            {key: 'Ctrl + Shift + Arrow', description: this.translate('scenes.resize') + ' x 10(px)', hidden},

                            {key: 'Ctrl + O', description: this.translate('general.orientVertical'), hidden},
                            {key: 'Ctrl + Shift + O', description: this.translate('general.orientHorizontal'), hidden},
                            {key: 'Ctrl + Shift + S', description: this.translate('general.showAll'), hidden},
                            {key: 'Ctrl + Shift + H', description: this.translate('general.hideItem'), hidden},
                        ]
                    },
                    {
                        group: 'touch / slides',
                        description: 'touch and slide navigation',
                        keys: [
                            {key: 'Home', description: 'Jump to first slide'},
                            {key: 'End', description: 'Jump to last slide'},
                            {key: 'Left', description: 'Previous slide'},
                            {key: 'Right', description: 'Next slide'},
                            {key: 'Page Up', description: 'Previous slide'},
                            {key: 'Page Down', description: 'Next slide'}
                        ]
                    }
                ];
                break;
            }
            case 'scene':
            case 'painter': {
                const hidden = this.hotkeyMode === 'painter';
                this.hotkeys = [
                    {
                        group: this.translate('general.general'),
                        description: 'general hotkeys',
                        keys: [
                            {key: 'Ctrl + C', description: this.translate('general.copy')},
                            {key: 'Ctrl + X', description: this.translate('general.cut')},
                            {key: 'Ctrl + V', description: this.translate('general.paste')},
        
                            {key: 'Ctrl + Z', description: this.translate('general.undo')},
                            {key: 'Ctrl + Y', description: this.translate('general.redo')},
        
                            {key: 'Alt + Plus', description: this.translate('general.zoomIn')},
                            {key: 'Alt + Minus', description: this.translate('general.zoomOut')},
        
                            {key: 'Ctrl + L', description: this.translate('general.toggleGuide'), hidden},
                            {key: 'Ctrl + ?', description: this.translate('general.keyboard')},
                            {key: 'Esc', description: this.translate('general.selectScene')}
                        ]
                    },
                    {
                        group: this.translate('general.items'),
                        description: 'selected items hotkeys',
                        keys: [
                            {key: 'Ctrl, Shift + Mouse', description: this.translate('general.addToSelection2'), hidden},
                            {key: 'Delete', description: this.translate('general.removeItems')},
                            {key: 'Alt + T', description: this.translate('general.addLabel'), hidden},
                            {key: 'Alt + P', description: this.translate('general.goBackPreviousScene'), hidden},
        
                            {key: 'Alt + Up', description: this.translate('general.pushUp')},
                            {key: 'Alt + Down', description: this.translate('general.pushDown')},
                            {key: 'Alt + Shift + Up', description: this.translate('general.pushTop')},
                            {key: 'Alt + Shift + Down', description: this.translate('general.pushBottom')},
        
                            {key: 'Alt + LeftRight', description: this.translate('general.rotate') + ' (1&#176;)'},
                            {key: 'Alt + Shift + LeftRight', description: this.translate('general.rotate') + ' (45&#176;)'},
                            {key: 'Arrow', description: this.translate('scenes.move')},
                            {key: 'Shift + Arrow', description: this.translate('scenes.move') + ' x 10(px)'},
                            {key: 'Ctrl + Arrow', description: this.translate('scenes.resize'), hidden},
                            {key: 'Ctrl + Shift + Arrow', description: this.translate('scenes.resize') + ' x 10(px)', hidden},
        
                            {key: 'Ctrl + O', description: this.translate('general.orientVertical'), hidden},
                            {key: 'Ctrl + Shift + O', description: this.translate('general.orientHorizontal'), hidden},
                            {key: 'Ctrl + Shift + S', description: this.translate('general.showAll'), hidden},
                            {key: 'Ctrl + Shift + H', description: this.translate('general.hideItem'), hidden},
                        ]
                    },
                ];
                break;
            }
            case 'timeline':
            case 'scheduler': {
                this.hotkeys = [
                    {
                        group: '',
                        description: 'general hotkeys',
                        keys: [
                            {key: 'Ctrl + V', description: this.translate('general.duplicate')},        
                            {key: 'Ctrl + Z', description: this.translate('general.undo')},
                            {key: 'Ctrl + Y', description: this.translate('general.redo')},
                            {key: 'Alt + Plus', description: this.translate('general.zoomIn')},
                            {key: 'Alt + Minus', description: this.translate('general.zoomOut')},
                            {key: 'Alt + S', description: this.translate('general.toggleSpecialDay')},
                            {key: 'Ctrl + L', description: this.translate('general.lowPriority')},
                            {key: 'Ctrl + M', description: this.translate('general.mediumPriority')},
                            {key: 'Ctrl + H', description: this.translate('general.highPriority')},

                            // {key: 'Alt + O', description: `${this.translate('general.repeatType')}: ${this.translate('general.once')} (incoming...)`},
                            // {key: 'Alt + D', description: `${this.translate('general.repeatType')}: ${this.translate('general.daily')} (incoming...)`},
                            // {key: 'Alt + W', description: `${this.translate('general.repeatType')}: ${this.translate('general.weekly')} (incoming...)`},
                            // {key: 'Alt + M', description: `${this.translate('general.repeatType')}: ${this.translate('general.monthly')} (incoming...)`},
                            // {key: 'Alt + Y', description: `${this.translate('general.repeatType')}: ${this.translate('general.yearly')} (incoming...)`},

                            // {key: 'Alt + Arrow', description: 'select (incoming...)'},
                            // {key: 'Shift + Arrow', description: 'resize (incoming...)'},
                            // {key: 'Ctrl + Arrow', description: 'drag (incoming...)'},

                            {key: 'Ctrl + ?', description: this.translate('general.keyboard')}
                        ]
                    }
                ];
                break;
            }
        }
        this.parseHotkeys();
    }

    private translate(terms) {
        return this.translocoService.translate(terms, {}, this.translocoService.getActiveLang());
    }

    private parseHotkeys() {
        let isMac = false;
        if (navigator['userAgentData']) {
            isMac = navigator['userAgentData']?.platform?.includes('Mac');
        }

        const symbols = {
            Ctrl: isMac ? '&#8984;' : 'Ctrl', // ⌘
            Left: '&#129092;', // ←
            Right: '&#129094;', // →
            Up: '&#129093;', // ↑
            Down: '&#129095;', // ↓
            Arrow: '&#10016;',
            LeftRight: '&#8646;',
            UpDown: '&#8645;',
            Plus: '&#10010;',
            Minus: '&#9472;'
        };

        for (const group of this.hotkeys) {
            for (const item of group['keys']) {
                Object.keys(symbols).forEach(symbol => {
                    item['key'] = item['key'].replace(new RegExp("\\b" + symbol + "\\b", "ig"), symbols[symbol]);
                });
            }
        }
    }

    ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }
}
